import type { SvgIconComponent } from "@mui/icons-material";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { Box, useTheme } from "@mui/material";
import type { TFunction } from "i18next";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { ACTION_NAME_TONE, BULK_UPDATE_ACTION_NAME, formatActivityLogWhen } from "@/pages/ActivityLog/GroupedActivityLog/activityLogGroupedDisplay";
import { type GroupedActivityLogEntry, ShiftOrigin } from "@/pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";
import { INK } from "@/theme/tokens";
import type { AccentIconTileTone } from "@/ui/molecules/AccentIconTile";
import { FocalCard } from "@/ui/molecules/FocalCard";
import { ExplainerCard } from "@/ui/Panels/ExplainerCard";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { actionIcon, describeEntry, ORIGIN_TITLE_KEY } from "./shiftHistoryDisplay";

type Props = {
	/** The entry whose transaction added the shift; undefined when that isn't in the Activity Log. */
	entry: GroupedActivityLogEntry | undefined;
	isRecurring: boolean;
	nurseName: string;
	/** Opens the recurring shift editor; undefined where the surface has no editor or the department locks it. */
	onEditRecurring: (() => void) | undefined;
	dataTestId: string;
};

type OriginCopy = { title: string; lines: string[] };

const present = (lines: (string | null)[]): string[] => lines.filter((line): line is string => line !== null);

const bulkLine = (entry: GroupedActivityLogEntry, t: TFunction): string | null =>
	entry.kind === "deferred" || entry.actionName === BULK_UPDATE_ACTION_NAME ? t("shiftHistory.origin.bulk.detail", { count: entry.groupSize }) : null;

const originCopy = (entry: GroupedActivityLogEntry | undefined, isRecurring: boolean, nurse: string, timezone: string, t: TFunction): OriginCopy => {
	if (entry === undefined) {
		return isRecurring
			? { title: t("shiftHistory.origin.recurring.linked", { nurse }), lines: [t("shiftHistory.origin.recurring.unrecorded")] }
			: { title: t("shiftHistory.origin.unrecorded.title"), lines: [t("shiftHistory.origin.unrecorded.detail")] };
	}
	if (entry.shiftOrigin === null || entry.shiftOrigin === undefined) throw new Error(`Shift history entry ${entry.apiRequestId} added the shift but not how`);
	const title = t(ORIGIN_TITLE_KEY[entry.shiftOrigin], { nurse });
	// A shift linked to a recurring schedule after it was added still belongs to, and opens, that schedule.
	const nowLinked = isRecurring && entry.shiftOrigin !== ShiftOrigin.Recurring ? t("shiftHistory.origin.recurring.nowLinked", { nurse }) : null;
	const bulk = bulkLine(entry, t);
	if (entry.kind === "deferred") return { title, lines: present([bulk, formatActivityLogWhen(entry.changedAt, timezone), nowLinked]) };
	const { what, summary, meta } = describeEntry(entry, timezone, t);
	const whatLine = summary === null ? what : `${what} · ${summary}`;
	return { title, lines: present([bulk === null ? whatLine : bulk, meta, nowLinked]) };
};

const originLook = (entry: GroupedActivityLogEntry | undefined, isRecurring: boolean): { Icon: SvgIconComponent; tone: AccentIconTileTone } => {
	if (entry === undefined) return isRecurring ? { Icon: EventRepeatRoundedIcon, tone: "info" } : { Icon: HelpOutlineRoundedIcon, tone: "neutral" };
	if (entry.shiftOrigin === ShiftOrigin.Recurring) return { Icon: EventRepeatRoundedIcon, tone: "info" };
	const actionName = entry.kind === "resolved" ? entry.actionName : BULK_UPDATE_ACTION_NAME;
	return { Icon: actionIcon(actionName), tone: ACTION_NAME_TONE[actionName] };
};

/** The history's focal moment: how the shift got on the schedule, and the way into its recurring schedule. */
export const ShiftOriginCard: FC<Props> = ({ entry, isRecurring, nurseName, onEditRecurring, dataTestId }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { chosenDepartment } = useHospitalData();
	const { title, lines } = originCopy(entry, isRecurring, nurseName, chosenDepartment.timezone, t);
	const { Icon, tone } = originLook(entry, isRecurring);
	const ink = tone === "neutral" ? INK.SECONDARY : theme.palette[tone].main;

	return (
		<FocalCard ink={ink} dataTestId={dataTestId} sx={{ mb: 3.5 }}>
			<ExplainerCard
				icon={<Icon />}
				tone={tone}
				eyebrow={t("shiftHistory.origin.label")}
				title={<Box data-testid={`${dataTestId}-title`}>{title}</Box>}
				width="fill"
			>
				{lines.map((line, index) => (
					<Box key={line} component="p" data-testid={`${dataTestId}-line-${index}`} sx={{ m: 0, color: index === 0 ? INK.SECONDARY : INK.MUTED }}>
						<bdi>{line}</bdi>
					</Box>
				))}
				{isRecurring && onEditRecurring !== undefined && (
					<Box sx={{ mt: 1.5 }}>
						<SystemButton buttonVariant="hairline" size="small" onClick={onEditRecurring} data-testid={`${dataTestId}-edit-recurring`}>
							{t("edit.recurring.shift")}
						</SystemButton>
					</Box>
				)}
			</ExplainerCard>
		</FocalCard>
	);
};
